<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import useLoading from '../../../../Composables/useLoading.ts'
import useModal from '../../../../Composables/useModal.ts'
import FormCreator from '../UI/Form/FormCreator.vue'
import LazyContent from '../UI/Loading/LazyContent.vue'

type FormCreatorType = InstanceType<typeof FormCreator>
type FormDefParameters = {
  entityId?: number
}

const props = defineProps<{
  entityId: number
  billingCode?: BillingCode | null
}>()

const emit = defineEmits<{
  close: []
  save: [{ billingCode: BillingCode }]
}>()

const { t } = useI18n()
const { loading, setLoading } = useLoading(true)
const { loading: sending } = useLoading(false)
const { modal, modalId, open, close } = useModal({ prefix: 'billing-code-' })

const form = ref<FormCreatorType | null>(null)

const modalTitle = computed(() =>
  props.billingCode ? t('entity.action.editBillingCode') : t('entity.action.addBillingCode'),
)
const defParams = computed<FormDefParameters>(() => (props.billingCode?.id ? {} : { entityId: props.entityId }))

async function submit() {
  sending.value = true
  try {
    await form.value?.methods()
  } finally {
    sending.value = false
  }
}

function submitted(billingCode: BillingCode) {
  emit('save', { billingCode })
  close()
}

function onFormLoad(formCreator: FormCreatorType) {
  form.value = formCreator
}

onMounted(() => {
  open()
})
</script>

<template>
  <ModalDialog :id="modalId" ref="modal" :title="modalTitle" class-body="px-3" @icon-close="emit('close')">
    <template #body>
      <FormCreator
        :id="billingCode?.id"
        endpoint="billing-codes"
        :params="defParams"
        send-params
        src-error="formbillingcode"
        class-to-add="d-none"
        @form-return="submitted"
        @update-data="onFormLoad"
        @is-loading="setLoading"
      />

      <LazyContent :loading="loading" :class="{ 'p-2': loading }">
        <div v-if="form" class="row">
          <div class="col-6">
            <FieldCreator name="active" form-ref="formbillingcode" :form-data="form" domain="billingCode" label-top />
          </div>
          <div class="col-6">
            <FieldCreator name="default" form-ref="formbillingcode" :form-data="form" domain="billingCode" label-top />
          </div>
          <div class="col-12">
            <FieldCreator name="code" form-ref="formbillingcode" :form-data="form" domain="billingCode" label-top />
          </div>
          <div class="col-12">
            <FieldCreator name="address" form-ref="formbillingcode" :form-data="form" domain="billingCode" label-top />
          </div>
        </div>
      </LazyContent>
    </template>
    <template v-if="!loading" #footer>
      <div class="d-flex w-100 justify-content-end">
        <ButtonAll margin="no" type="default" label="button.cancel" @click="close" />
        <ButtonAll margin="no" type="action" label="button.confirm" class="ml-2" :loading="sending" @click="submit" />
      </div>
    </template>
  </ModalDialog>
</template>
